/**
 * Runtime polyfills for browsers commonly used in India:
 * Chrome/Android WebView 80–111, Samsung Internet, UC Browser, Opera,
 * Firefox Android, and Safari iOS 13–15.
 *
 * Loaded before React so later modules (pdf.js, Firebase, book viewers)
 * can assume these APIs exist.
 */

type CompatWindow = Window & {
  requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number;
  cancelIdleCallback?: (id: number) => void;
};

function randomUUID(): string {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoObj.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `rd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function installCryptoRandomUUID(): void {
  const cryptoObj = globalThis.crypto as Crypto | undefined;
  if (!cryptoObj || typeof cryptoObj.randomUUID === 'function') return;
  try {
    Object.defineProperty(cryptoObj, 'randomUUID', {
      configurable: true,
      writable: true,
      value: randomUUID as Crypto['randomUUID'],
    });
  } catch {
    try {
      (cryptoObj as { randomUUID: Crypto['randomUUID'] }).randomUUID =
        randomUUID as Crypto['randomUUID'];
    } catch {
      // Safari private / frozen crypto object — callers still have the export below.
    }
  }
}

function installMapGetOrInsertComputed(): void {
  const proto = Map.prototype as Map<unknown, unknown> & {
    getOrInsertComputed?: <K, V>(key: K, callbackfn: (key: K) => V) => V;
  };
  if (typeof proto.getOrInsertComputed === 'function') return;
  proto.getOrInsertComputed = function <K, V>(this: Map<K, V>, key: K, callbackfn: (key: K) => V): V {
    if (this.has(key)) return this.get(key) as V;
    const value = callbackfn(key);
    this.set(key, value);
    return value;
  };
}

function installObjectHasOwn(): void {
  const objectCtor = Object as ObjectConstructor & {
    hasOwn?: (obj: object, prop: PropertyKey) => boolean;
  };
  if (typeof objectCtor.hasOwn === 'function') return;
  objectCtor.hasOwn = function hasOwn(obj: object, prop: PropertyKey): boolean {
    return Object.prototype.hasOwnProperty.call(obj, prop);
  };
}

function installArrayAt(): void {
  const proto = Array.prototype as unknown as { at?: <T>(this: ArrayLike<T>, index: number) => T | undefined };
  if (typeof proto.at === 'function') return;
  Object.defineProperty(Array.prototype, 'at', {
    configurable: true,
    writable: true,
    value: function at<T>(this: ArrayLike<T>, index: number): T | undefined {
      const len = this.length >>> 0;
      const i = Math.trunc(index);
      const k = i >= 0 ? i : len + i;
      if (k < 0 || k >= len) return undefined;
      return this[k];
    },
  });
}

function installStringReplaceAll(): void {
  const proto = String.prototype as unknown as { replaceAll?: (search: string | RegExp, replacement: string) => string };
  if (typeof proto.replaceAll === 'function') return;
  Object.defineProperty(String.prototype, 'replaceAll', {
    configurable: true,
    writable: true,
    value: function replaceAll(this: string, search: string | RegExp, replacement: string): string {
      if (search instanceof RegExp) {
        if (!search.global) {
          throw new TypeError('replaceAll with a RegExp requires the global flag');
        }
        return this.replace(search, replacement);
      }
      return this.split(search).join(replacement);
    },
  });
}

function installIdleCallback(): void {
  const w = window as CompatWindow;
  if (typeof w.requestIdleCallback === 'function') return;
  w.requestIdleCallback = (cb) =>
    window.setTimeout(() => {
      const start = Date.now();
      cb({
        didTimeout: false,
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, 1);
  w.cancelIdleCallback = (id) => window.clearTimeout(id);
}

function installMediaQueryListEvent(): void {
  const proto = typeof MediaQueryList !== 'undefined' ? MediaQueryList.prototype : null;
  if (!proto) return;
  if (typeof proto.addEventListener === 'function') return;

  proto.addEventListener = function (
    this: MediaQueryList,
    type: string,
    listener: EventListenerOrEventListenerObject,
  ) {
    if (type !== 'change') return;
    const handler = listener as (ev: MediaQueryListEvent) => void;
    // Safari 13 / older Samsung Internet
    (this as MediaQueryList & { addListener?: (l: (ev: MediaQueryListEvent) => void) => void }).addListener?.(handler);
  };

  proto.removeEventListener = function (
    this: MediaQueryList,
    type: string,
    listener: EventListenerOrEventListenerObject,
  ) {
    if (type !== 'change') return;
    const handler = listener as (ev: MediaQueryListEvent) => void;
    (this as MediaQueryList & { removeListener?: (l: (ev: MediaQueryListEvent) => void) => void }).removeListener?.(handler);
  };
}

function installAbortSignalTimeout(): void {
  if (typeof AbortSignal === 'undefined') return;
  if (typeof AbortSignal.timeout === 'function') return;
  Object.defineProperty(AbortSignal, 'timeout', {
    configurable: true,
    writable: true,
    value: function timeout(ms: number): AbortSignal {
      const controller = new AbortController();
      window.setTimeout(() => controller.abort(), ms);
      return controller.signal;
    },
  });
}

function installSmoothScrollFallback(): void {
  const originalScrollTo = window.scrollTo.bind(window);
  window.scrollTo = function scrollTo(...args: unknown[]) {
    try {
      return (originalScrollTo as (...a: unknown[]) => void)(...args);
    } catch {
      const opts = args[0] as { top?: number; left?: number } | number | undefined;
      if (opts && typeof opts === 'object') {
        originalScrollTo(opts.left ?? 0, opts.top ?? 0);
        return;
      }
    }
  } as typeof window.scrollTo;
}

/** Safe localStorage accessors — Safari private mode and some WebViews throw. */
export function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function storageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // quota / private mode
  }
}

export { randomUUID };

export function installBrowserCompat(): void {
  if (typeof window === 'undefined') return;
  installCryptoRandomUUID();
  installMapGetOrInsertComputed();
  installObjectHasOwn();
  installArrayAt();
  installStringReplaceAll();
  installIdleCallback();
  installMediaQueryListEvent();
  installAbortSignalTimeout();
  installSmoothScrollFallback();
}

// Side-effect: run on first import so pdf.js / Firebase see the APIs.
installBrowserCompat();
